/* eslint-disable no-unused-vars */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import SettingsIcon from "src/assets/all-icons/settings-icon.jsx";
import { v4 as uuidv4 } from 'uuid';

import config from '@/config';
import useStateHandler from '@/hooks/useStateHandler';
import ConversationInput from '@/pages/orders/order-details/conversation/components/ConversationInput';
import EmptyChannel from '@/pages/profile/channels/EmptyChannel';
import fetchWrapper from '@/services/fetchWrapper.service';
import UserService from '@/services/user.service';
import ChatMessage from '@/shared-components/cards/components/ChatMessage';
import ChatTimestamp from '@/shared-components/cards/components/ChatTimestamp';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import BackArrowIcon from '@/shared-components/icons/components/public/BackArrowIcon';
import Utils from '@/utils';
import { resolveUserProfileImage, resolveUserProfileTypeWithFallback } from '@/utils/userProfileImage';

import '@/pages/profile/channels/styles/ChannelChat.css'; // Reuse existing CSS

function pickFirstNonEmptyString(...values) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }
  return null;
}

export default function DirectMessagesChat() {
  // Extract DM identifier from URL.
  const { user_username } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { state, stateHandler } = useStateHandler();
  const stateHandlerRef = useRef(stateHandler);
  useEffect(() => {
    stateHandlerRef.current = stateHandler;
  }, [stateHandler]);
  const setChannelUnreadUpdated = useCallback((value) => {
    const handler = stateHandlerRef.current;
    if (!handler) return;
    handler.set('channelUnreadUpdated', value);
  }, []);

  // Determine if the logged-in user is the owner of the DM.
  const isOwner =
    state &&
    state.user_session &&
    state.user_session.user_username === user_username;

  // Add "chat-page" CSS class to the body.
  useEffect(() => {
    document.body.classList.add("chat-page");
    return () => document.body.classList.remove("chat-page");
  }, []);

  // Local state for messages, loading, error, DM name, DM image, and unread IDs.
  const [messages, setMessages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dmName, setDmName] = useState('');
  const [dmImage, setDmImage] = useState(null);
  const [dmProfileType, setDmProfileType] = useState('collector');
  const [unreadMessageIds, setUnreadMessageIds] = useState([]);

  // Reference for auto-scrolling.
  const messagesContainerRef = useRef(null);

  // Clear header name while fetching the next profile.
  useEffect(() => {
    setDmName('');
  }, [user_username]);

  // Helper function to fetch the DM owner's profile.
  function fetchProfile() {
    const endpoint = isOwner
      ? `${config.apis.api.url}/user/me`
      : `${config.apis.api.url}/user/public/${encodeURIComponent(user_username)}`;
    fetchWrapper
      .get(endpoint, { injectToken: true })
      .then(({ response, data }) => {
        const profileData = data?.data && typeof data.data === 'object' ? data.data : data;
        const name = pickFirstNonEmptyString(
          profileData?.user_name,
          profileData?.channel_name,
        );
        const image = resolveUserProfileImage(
          profileData?.user_image,
          profileData?.profile_image,
          profileData?.image,
        );
        const type = resolveUserProfileTypeWithFallback(
          'collector',
          profileData?.user_type,
          profileData?.__t,
          profileData?.type,
        );
        setDmName(name || '');
        setDmImage(image);
        setDmProfileType(type);
      })
      .catch(() => {
        setDmName('');
        setDmImage(null);
        setDmProfileType('collector');
      });
  }

  // Fetch profile on mount and when DM identifier changes.
  useEffect(() => {
    fetchProfile();
  }, [user_username]);

  // Re-fetch profile when window regains focus.
  useEffect(() => {
    const onFocus = () => fetchProfile();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [user_username]);

  // Fetch DM conversation messages.
  useEffect(() => {
    if (!user_username) {
      setLoading(false);
      return;
    }
    setLoading(true);
    // Use DM endpoint; ensure UserService.getChatConversation exists.
    UserService.getChatConversation(user_username)
      .then(({ response, data }) => {
        if (response.ok) {
          const msgs = data.data;
          msgs.reverse();
          setMessages({
            data: msgs,
            totalDocs: data.totalDocs ?? (data.messages ? data.messages.length : 0)
          });
          setError(null);
          // Identify unread messages.
          const unreadIds = msgs.filter(({ viewed }) => !viewed).map(({ _id }) => _id);
          setUnreadMessageIds(unreadIds);
        } else if (response.status === 404) {
          setMessages({ data: [], totalDocs: 0 });
          setError(null);
        } else {
          setError("Failed to load conversation.");
        }
      })
      .catch(() => {
        setError("Failed to load conversation.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [user_username]);

  // Auto-scroll messages container when messages update.
  useEffect(() => {
    if (
      messagesContainerRef.current &&
      messages &&
      Array.isArray(messages.data) &&
      messages.data.length > 0
    ) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Mark messages as read.
  useEffect(() => {
    if (unreadMessageIds.length > 0) {
      UserService.markMessageAsRead(user_username, unreadMessageIds)
        .then(({ response, data }) => {
          setMessages(prev => ({
            ...prev,
            data: prev.data.map(msg =>
              unreadMessageIds.includes(msg._id) ? { ...msg, viewed: true } : msg
            )
          }));
          setChannelUnreadUpdated(true);
          setUnreadMessageIds([]);
        })
        .catch((err) => {
          console.error("Error in markMessageAsRead:", err);
        });
    }
  }, [unreadMessageIds, user_username, setChannelUnreadUpdated]);

  // Function to send a new DM message.
  const sendMessage = async (messageText) => {
    if (!user_username || messageText.trim().length === 0) return;
    const messageCreated = {
      _id: uuidv4(),
      message_sender: state.user_session,
      createdAt: Date.now(),
      message_text: messageText,
    };
    setMessages(prev => ({
      ...prev,
      data: [...(prev?.data || []), messageCreated],
      totalDocs: (prev?.totalDocs || 0) + 1,
    }));
    try {
      // Use DM endpoint; ensure UserService.postChatMessage exists.
      const { response, data } = await UserService.postChatMessage(user_username, messageText);
      if (response.ok && data.message) {
        setMessages(prev => {
          const newData = prev.data.map(msg =>
            msg._id === messageCreated._id ? data.message : msg
          );
          return { ...prev, data: newData };
        });
      } else if (response.status === 401) {
        setError("Unauthorized. Please ensure you are logged in.");
      }
    } catch (err) {
      setError("Failed to send message.");
    }
  };

  // Determine if a timestamp should be rendered.
  function shouldRenderTimestamp(index) {
    if (index === 0) return true;
    const currentTime = new Date(messages.data[index].createdAt).getTime();
    const previousTime = new Date(messages.data[index - 1].createdAt).getTime();
    return !Utils.isSameDay(currentTime, previousTime);
  }

  // Header for the DM chat.
  const header = (
    <div className="channel-chat__header">
      <BackArrowIcon
        className="channel-chat__back-button"
        onClick={() => navigate(-1)}
      />
      <Link className="no-underline" to={`/user/${user_username}`} state={{ from: true }}>
        <UserProfileImage image={dmImage} typeOfProfile={dmProfileType} className="chat-message__user-image" />
      </Link>
      <h2>{dmName}</h2>
      {isOwner && (
        <button type="button" className="channel-chat__settings-link" onClick={() => navigate(`/chat/dm/${user_username}/preferences`)}>
          <SettingsIcon />
        </button>
      )}
    </div>
  );

  // ConversationInput already renders the "artwork-view-thoughts__input-container conversation" class.
  const inputContainer = <ConversationInput sendMessage={sendMessage} />;

  // In a flex column container (channel-chat__container), the middle area must have flex-grow.
  // For loading, error, and empty states, wrap the middle content in a container with style={{ flex: 1 }}.

  if (loading) {
    return (
      <div className="channel-chat__container">
        {header}
        <div style={{ flex: 1 }}></div>
        {inputContainer}
      </div>
    );
  }
  if (error) {
    return (
      <div className="channel-chat__container">
        {header}
        <div style={{ flex: 1 }}>
          <div className="channel-chat__error">{error}</div>
        </div>
        {inputContainer}
      </div>
    );
  }
  if (!messages || !Array.isArray(messages.data) || messages.data.length === 0) {
    return (
      <div className="channel-chat__container">
        {header}
        <div style={{ flex: 1 }}>
          <EmptyChannel channelData={{ user_username }} />
        </div>
        {inputContainer}
      </div>
    );
  }

  return (
    <div className="channel-chat__container">
      {header}
      <div className="channel-chat__messages" ref={messagesContainerRef}>
        {messages.data.map((message, index) => (
          <div key={message._id || index} style={{ paddingBottom: '15px' }}>
            {shouldRenderTimestamp(index) && (
              <div style={{ paddingBottom: '15px' }}>
                <ChatTimestamp timestamp={new Date(message.createdAt).getTime()} />
              </div>
            )}
            <ChatMessage messageData={message} showAvatar={true} isMineRight={true} />
          </div>
        ))}
      </div>
      {inputContainer}
    </div>
  );
}
